/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';

import { css } from '@emotion/core';

import BulmaButton from 'react-bulma-components/lib/components/button';
import BulmaContainer from 'react-bulma-components/lib/components/container';
import BulmaSection from 'react-bulma-components/lib/components/section';
import BulmaCard from 'react-bulma-components/lib/components/card';

import { RichText } from 'prismic-reactjs';
import { linkResolver } from '../../util/linkResolver';
import { createRandomId } from '../../util/createRandomId';
import { Heading, Button, LazyLink, LazyImage } from '../atoms';

import {
  styledHeading,
  styledSubHeading,
  styledButtonContainer,
  styledCenterHeroText,
  styledHeroTextSection,
  styledHeroTextFullscreen,
  HeroTextContentContainer,
  HeroTextFormContainer,
  heroTextNoForm,
  heroTextForm,
  styledHeroBox,
  styledHeroBoxTitle,
  StyledForm,
  styledButton,
  styledSubmit,
  styledButtonWText,
  StyledSubscript,
  StyledButtonsContainer,
  StyledButtonsContainerHr,
} from './HeroTextStyles';

const renderButton = (link, text, atts) => (
  <LazyLink url={link.url} {...atts}>
    {text.text}
  </LazyLink>
);

const renderHeading = (text, atts) => <Heading {...atts}>{text.text}</Heading>;

const HeroText = ({ input }) => {
  const {
    fullscreen,
    large_text,
    text_color,
    background_color,
    background_image,
    heading,
    subheading,
    display_primary_cta,
    primary_cta_color,
    primary_cta_link,
    primary_cta_text,
    display_secondary_cta,
    secondary_cta_link,
    secondary_cta_text,
    text_alignment,
    email_placeholder,
    form_title,
    name_placeholder,
    password_placeholder,
    submit_button_text,
    subscript,
  } = input.primary;

  const { fields } = input;

  const [careersPage, setCareersPage] = useState(false);

  const onFormSubmit = event => {
    event.preventDefault();

    window.optimizely.push({
      type: 'event',
      eventName: 'clicked_signup_button',
      tags: {
        action: 'click',
        category: 'form',
        name: 'signup',
      },
    });

    window.analytics.track('Web Interaction', {
      action: 'click',
      category: 'form',
      name: 'signup',
    });

    if (document.getElementById('heroFormPricing')) {
      setTimeout(function() {
        document.getElementById('heroFormPricing').submit();
      }, 500);
    }
  };

  const onButtonClick = event => {
    const clickId = event.currentTarget.id.replace('_button', '');
    const googleIdCheck = clickId === 'Sign up with Google' ? 'google' : clickId;
    const dataHref = event.currentTarget.dataset.href;
    window.optimizely.push({
      type: 'event',
      eventName: 'clicked_signup_button',
      tags: {
        action: 'click',
        category: googleIdCheck,
        name: 'signup',
      },
    });

    window.analytics.track('Web Interaction', {
      action: 'click',
      category: googleIdCheck,
      name: 'signup',
    });

    if (dataHref) {
      setTimeout(function() {
        window.location.href = dataHref;
      }, 500);
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined' || !window.document) {
      // return
    } else {
      const isCareers = document.location.pathname.includes('/careers/');

      setCareersPage(isCareers);

      if (form_title) {
        window.analytics.page('Pricing', {
          form_loaded: true,
        });
      }
    }
  }, [careersPage]);

  return (
    <span>
      {careersPage ? (
        <Helmet>
          <script src="https://widget.altrulabs.com/main.js" data-altru-widget-id="5694" />
        </Helmet>
      ) : null}
      <BulmaSection
        css={css`
          ${styledHeroTextSection}
          background-color: ${background_color};
          ${background_image ? `background-image: url(${background_image.url});` : null};
          ${text_alignment === 'center' ? 'text-align: center;' : 'text-align: left;'}
          ${fullscreen && styledHeroTextFullscreen}
        `}
      >
        <BulmaContainer css={text_alignment === 'center' ? styledCenterHeroText : null}>
          <HeroTextContentContainer>
            <div
              css={form_title ? heroTextForm : heroTextNoForm}
              style={text_alignment === 'left' ? { textAlign: 'left' } : null}
            >
              {heading && heading[0]
                ? renderHeading(heading[0], {
                    id: 'herotext-heading',
                    className: `${text_color === 'light' ? 'h1 white' : 'h1 darkblue'} ${
                      large_text ? 'hero-title' : ''
                    }`,
                    css: styledHeading,
                    style: { margin: text_alignment === 'left' ? '0' : '' },
                  })
                : null}
              {subheading && subheading[0]
                ? renderHeading(subheading[0], {
                    id: 'herotext-subheading',
                    className: `${text_color === 'light' ? 'large white' : 'large darkblue'}`,
                    renderAs: 'p',
                    subtitle: true,
                    css: styledSubHeading,
                    style: { margin: text_alignment === 'left' ? '0' : '' },
                  })
                : null}
              {display_primary_cta || display_secondary_cta ? (
                <BulmaButton.Group
                  id="herotext-buttons"
                  style={text_alignment === 'center' ? { display: 'flex', justifyContent: 'center' } : null}
                  css={styledButtonContainer}
                >
                  {display_primary_cta && primary_cta_text && primary_cta_link
                    ? renderButton(primary_cta_link, primary_cta_text[0], {
                        className: `${
                          primary_cta_color === 'white'
                            ? 'button-link is-white is-outlined'
                            : 'button-link is-primary is-outlined'
                        }`,
                      })
                    : null}
                  {display_secondary_cta && secondary_cta_text && secondary_cta_link
                    ? renderButton(secondary_cta_link, secondary_cta_text[0], { className: 'button-link is-white' })
                    : null}
                </BulmaButton.Group>
              ) : null}
            </div>
            {form_title ? (
              <HeroTextFormContainer>
                <BulmaCard css={styledHeroBox} data-testid="hero_box">
                  <BulmaCard.Header.Title css={styledHeroBoxTitle}>
                    {renderHeading(form_title[0], {
                      className: 'h5 darkblue',
                      renderAs: 'h5',
                      'data-testid': 'hero_box_title',
                    })}
                  </BulmaCard.Header.Title>
                  <BulmaCard.Content>
                    <StyledForm
                      className="form-align-left"
                      action="https://cloud.digitalocean.com/registrations"
                      method="post"
                      id="heroFormPricing"
                      onSubmit={onFormSubmit}
                    >
                      <span className="form-wrapper">
                        <input
                          aria-label="signup button"
                          type="text"
                          minLength="2"
                          name="user[name]"
                          placeholder="First Name"
                          required
                        />
                        <input
                          aria-label="email"
                          type="email"
                          name="user[email]"
                          placeholder="Email Address"
                          required
                        />
                        <input
                          aria-label="password"
                          type="password"
                          minLength="8"
                          name="user[password]"
                          placeholder="Password"
                          required
                        />
                        <Button
                          className="is-primary"
                          css={styledSubmit}
                          type="submit"
                          onClick={onButtonClick}
                          id="form_button"
                        >
                          {submit_button_text}
                        </Button>
                      </span>
                    </StyledForm>
                  </BulmaCard.Content>
                  <StyledButtonsContainerHr />
                  <StyledButtonsContainer>
                    {fields.map(button => (
                      <Button
                        className="is-primary is-outlined"
                        css={button.button_text !== null ? styledButtonWText : styledButton}
                        key={createRandomId()}
                        onClick={onButtonClick}
                        data-href={button.button_link.url}
                        id={button.button_text || 'github_button'}
                        aria-label={button.button_text || 'signup_button'}
                      >
                        <LazyImage src={button.icon.url} alt={button.icon.alt || button.button_text || 'icon'} />
                        {button.button_text}
                      </Button>
                    ))}
                  </StyledButtonsContainer>
                </BulmaCard>
                <StyledSubscript>
                  <RichText render={subscript} linkResolver={linkResolver} />
                </StyledSubscript>
              </HeroTextFormContainer>
            ) : null}
          </HeroTextContentContainer>
        </BulmaContainer>
      </BulmaSection>
    </span>
  );
};

export default HeroText;

HeroText.propTypes = {
  input: PropTypes.object.isRequired,
};
